import React, { useState } from 'react';
import { KeyRound } from 'lucide-react';
import { Button } from '@components/Button';
import { Modal } from '@components/Dialog';
import { DateInputBR } from '@components/DateInputBR';
import { Checkbox, Select } from '@components/Input';
import profileService from '@services/profileService';
import { PLAN_LABELS, type AppPlan, type AppUser } from '@/types/planning';
import { formatDateBR, todayISO } from '@/utils/formatters';

interface Props {
  user: AppUser;
  onClose: () => void;
  onSaved: (updated: AppUser) => void;
  onError: (err: unknown) => void;
}

const labelCls = 'text-label block mb-1';
const PLANS: AppPlan[] = ['free', 'trial', 'paid'];

/**
 * "Acesso" de um usuário (PATCH /admin/users/:id { plan, access_until }): plano (gratuito, teste ou
 * pago) e data de término do acesso — ou "sem data de término" (`access_until: null`). Estender a
 * data de quem estava expirado enfileira o e-mail `access_extended` (regra do backend).
 */
export const UserAccessModal: React.FC<Props> = ({ user, onClose, onSaved, onError }) => {
  const [plan, setPlan] = useState<AppPlan>(user.plan ?? 'free');
  const [noEnd, setNoEnd] = useState(!user.access_until);
  const [accessUntil, setAccessUntil] = useState(user.access_until ?? '');
  const [saving, setSaving] = useState(false);
  const [fieldError, setFieldError] = useState<string | null>(null);

  const save = async () => {
    if (!noEnd && !accessUntil) {
      setFieldError('Informe a data de término ou marque "Sem data de término".');
      return;
    }
    setFieldError(null);
    setSaving(true);
    try {
      const updated = await profileService.updateUser(user.user_id, {
        plan,
        access_until: noEnd ? null : accessUntil,
      });
      onSaved(updated);
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  const hint = noEnd
    ? 'Sem data: o acesso não expira (até você definir uma data ou desativar o usuário).'
    : accessUntil && accessUntil < todayISO()
      ? `Atenção: ${formatDateBR(accessUntil)} já passou — o usuário verá "acesso encerrado" na próxima requisição.`
      : accessUntil
        ? `O acesso vale até ${formatDateBR(accessUntil)} (inclusive).`
        : '';

  return (
    <Modal isOpen onClose={onClose} size="sm" closeOnBackdrop={!saving}>
      <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
        <KeyRound size={18} className="text-slate-500" />
        <h2 className="text-h3 min-w-0 truncate">Acesso de {user.name}</h2>
      </div>
      <div className="px-6 py-4 max-h-[70vh] overflow-y-auto custom-scrollbar space-y-4">
        <p className="text-small [overflow-wrap:anywhere]">{user.email}</p>

        <Select label="Plano" value={plan} onChange={(e) => setPlan(e.target.value as AppPlan)} disabled={saving}>
          {PLANS.map((p) => (
            <option key={p} value={p}>
              {PLAN_LABELS[p]}
            </option>
          ))}
        </Select>

        <div>
          <label className={labelCls}>Acesso até</label>
          <DateInputBR
            value={noEnd ? '' : accessUntil}
            onChange={(iso) => {
              setAccessUntil(iso);
              setFieldError(null);
            }}
            disabled={saving || noEnd}
            className="input-field"
          />
          <div className="mt-2">
            <Checkbox
              label="Sem data de término"
              checked={noEnd}
              disabled={saving}
              onChange={(e) => {
                setNoEnd(e.target.checked);
                setFieldError(null);
              }}
            />
          </div>
          {fieldError ? <p className="text-sm text-red-600 mt-1">{fieldError}</p> : hint && <p className="text-small mt-1">{hint}</p>}
        </div>
      </div>
      <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex flex-wrap justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={save} loading={saving}>
          Salvar
        </Button>
      </div>
    </Modal>
  );
};

export default UserAccessModal;
